import { useState } from 'react';
import {
  FileText,
  History,
  Database,
  ExternalLink,
  Bookmark,
  BookmarkCheck,
  Share2,
  ChevronDown,
  ChevronUp,
  Calendar,
  Link2,
} from 'lucide-react';
import type { LegacyQuoteCard } from '@/types';

interface QuoteCardItemProps {
  card: LegacyQuoteCard;
  isSaved: boolean;
  onViewDetail: () => void;
  onAddToTrail: () => void;
  onShare: () => void;
  compact?: boolean;
}

const laneIcons = {
  A: FileText,
  B: History,
  C: Database,
};

const laneLabels = {
  A: 'Law & regulation',
  B: 'Official context',
  C: 'Public data',
};

const laneColors = {
  A: 'text-blue-600 bg-blue-50',
  B: 'text-amber-600 bg-amber-50',
  C: 'text-green-600 bg-green-50',
};

export function QuoteCardItem({
  card,
  isSaved,
  onViewDetail,
  onAddToTrail,
  onShare,
  compact = false,
}: QuoteCardItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const Icon = laneIcons[card.lane];

  return (
    <div
      className={`receipt-card overflow-hidden transition-all hover:shadow-lg ${compact ? 'p-4' : 'p-5'}`}
    >
      {/* Header */}
      <div className="mb-3 flex items-start justify-between">
        <div className="flex items-center gap-2">
          <div className={`rounded-lg p-1.5 ${laneColors[card.lane]}`}>
            <Icon className="h-4 w-4" />
          </div>
          <span className="font-mono text-xs uppercase tracking-[0.08em] text-[#6B6F78]">
            Lane {card.lane} — {laneLabels[card.lane]}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onAddToTrail}
            className={`rounded-lg p-2 transition-colors ${
              isSaved ? 'bg-[#E1062C]/10 text-[#E1062C]' : 'text-[#6B6F78] hover:bg-[#F6F5F2]'
            }`}
            aria-label={isSaved ? 'Saved to trail' : 'Add to trail'}
          >
            {isSaved ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
          </button>
          <button
            onClick={onShare}
            className="rounded-lg p-2 text-[#6B6F78] transition-colors hover:bg-[#F6F5F2]"
            aria-label="Share receipt"
          >
            <Share2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Quote */}
      <button type="button" onClick={onViewDetail} className="block w-full text-left">
        <p
          className={`mb-3 font-medium leading-relaxed text-[#0B0D10] ${compact ? 'text-sm' : 'text-base'}`}
        >
          {card.quote}
        </p>
      </button>

      {/* Source & Date */}
      <div className="flex items-center justify-between border-t border-[rgba(11,13,16,0.08)] pt-3">
        <div className="flex items-center gap-3">
          <span className="text-sm text-[#6B6F78]">{card.source}</span>
          <span className="font-mono text-xs text-[#E1062C]">{card.date}</span>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1 text-sm text-[#E1062C] hover:underline"
          aria-expanded={isExpanded}
        >
          {isExpanded ? (
            <>
              <ChevronUp className="h-4 w-4" />
              Less
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4" />
              Context
            </>
          )}
        </button>
      </div>

      {/* Expanded Context */}
      {isExpanded && card.context && (
        <div className="mt-4 rounded-xl border border-[rgba(11,13,16,0.08)] bg-[#F6F5F2] p-4">
          <p className="mb-3 text-sm leading-relaxed text-[#6B6F78]">{card.context}</p>
          {(card.effectiveDate || card.amendedBy) && (
            <div className="flex flex-wrap gap-2 border-t border-[rgba(11,13,16,0.08)] pt-3">
              {card.effectiveDate && (
                <span className="flex items-center gap-1 rounded bg-white px-2 py-1 text-xs text-[#6B6F78]">
                  <Calendar className="h-3 w-3" />
                  Effective: {card.effectiveDate}
                </span>
              )}
              {card.amendedBy && (
                <span className="flex items-center gap-1 rounded bg-white px-2 py-1 text-xs text-[#6B6F78]">
                  <Link2 className="h-3 w-3" />
                  Amended by: {card.amendedBy}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Canonical Link */}
      <div className="mt-3 flex items-center justify-between">
        <a
          href={card.canonicalLink}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-xs text-[#6B6F78] transition-colors hover:text-[#E1062C]"
        >
          <ExternalLink className="h-3 w-3" />
          Open source
        </a>
        <span className="font-mono text-xs text-[#9CA3AF]">{card.documentTitle}</span>
      </div>
    </div>
  );
}
